import React, {
    useEffect, 
    useMemo, 
    useState,
    useRef,
    useCallback 
} from "react";
import Button from "@/components/Base/Button";
import FormInput from "@/components/Base/Form/FormInput";
import FormSelect from "@/components/Base/Form/FormSelect";
import clsx from "clsx";
import Lucide from "@/components/Base/Lucide";
import axios from "axios";
import { getToken } from "@/utils/auth";
import { useNavigate, useParams } from "react-router-dom";
import Dropzone from "@/components/Base/Dropzone";
import { LucideFolderSymlink } from "lucide-react";
import Litepicker from "@/components/Base/Litepicker";


// Fix date parsing to avoid timezone shift
function parseLocalDateOnly(input: string): string {
    if (!input || typeof input !== "string") {
        throw new Error("Invalid date input: " + input);
    }

    const date = new Date(input);
    if (isNaN(date.getTime())) {
        throw new Error("Parsed invalid date from: " + input);
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-based
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD
}


type Mode = "add" | "edit";

interface Props {
    /** Optional override. If not provided, will auto-detect from route param `id` */
    mode?: Mode;
}

interface State {
    id: string;
    name: string;
}

interface FormData {
    firstName: string;
    lastName: string;
    username: string;
    password: string;
    roleId: string;
    status: string;
    mobileNo: string;
    email: string;
    staffId: string;
    position: string;
    department: string;
    category: string;
    joinDate: string;
    resignDate: string;
    companyId?: string;
    imageUrl?: string;
}

interface FormErrors {
    firstName?: string;
    lastName?: string;
    username?: string;
    password?: string;
    roleId?: string;
    status?: string;
    email?: string;
}

const EMPTY_FORM: FormData = {
    firstName: "",
    lastName: "",
    username: "",
    password: "",
    roleId: "",
    status: "true", // Default to Active
    mobileNo: "",
    email: "",
    staffId: "",
    position: "",
    department: "",
    category: "",
    joinDate: "",
    resignDate: "",
    companyId: "",
    imageUrl: "",
}

export default function EditUser({ mode: modeProp}: Props) {
    const { id } = useParams(); // expect /companies/:id/edit for edit
    const mode: Mode = useMemo(() => {
        if (modeProp) return modeProp;
        return id ? "edit" : "add";
    }, [modeProp, id]);

    const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
    const [roles, setRoles] = useState<{id: string, role: string}[]>([]);
    const [departments, setDepartments] = useState<{id: string, department: string}[]>([]);
    const [empCategories, setEmpCategories] = useState<{id: string, category: string}[]>([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(mode === "edit"); // load when editing
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [imageUrl, setImageUrl] = useState<string>("");
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [imageError, setImageError] = useState("");
    const dropzoneRef = useRef<any>();
    const navigate = useNavigate();

    // Validation functions
    const validateField = (name: string, value: string): string | undefined => {
        switch (name) {
            case 'firstName':
                return !value.trim() ? 'First name is required' : undefined;
            case 'lastName':
                return !value.trim() ? 'Last name is required' : undefined;
            case 'username':
                if (!value.trim()) return 'Username is required';
                if (value.length < 3) return 'Username must be at least 3 characters';
                return undefined;
            case 'password':
                if (mode === 'add' && !value.trim()) return 'Password is required';
                if (value && value.length < 8) return 'Password must be at least 8 characters';
                return undefined;
            case 'roleId':
                return !value.trim() ? 'Role is required' : undefined;
            case 'status':
                return !value.trim() ? 'Status is required' : undefined;
            case 'email':
                if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return 'Please enter a valid email address';
                }
                return undefined;
            default:
                return undefined;
        }
    };

    const validateForm = (): FormErrors => {
        const errors: FormErrors = {};
        
        // Validate required fields
        const requiredFields = ['firstName', 'lastName', 'username', 'roleId', 'status'];
        if (mode === 'add') requiredFields.push('password');
        
        requiredFields.forEach(field => {
            const error = validateField(field, formData[field as keyof FormData]);
            if (error) errors[field as keyof FormErrors] = error;
        });
        
        // Validate email if provided
        if (formData.email) {
            const emailError = validateField('email', formData.email);
            if (emailError) errors.email = emailError;
        }
        
        return errors;
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        
        // Clear field error when user starts typing
        if (formErrors[name as keyof FormErrors]) {
            setFormErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Handle image upload
    const handleImageUpload = useCallback(async (files: File[] | File) => {
        // Handle both single file and array of files
        const fileArray = Array.isArray(files) ? files : [files];

        if (fileArray.length === 0) return;

        const file = fileArray[0];

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setImageError('Please select an image file');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setImageError('Image size must be less than 5MB');
            return;
        }

        setIsUploadingImage(true);
        setImageError("");

        try {
            const formData = new FormData();
            formData.append('image', file);

            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            const response = await axios.post(`${apiUrl}/upload/image`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const uploadedImageUrl = response.data.data.url;
                setImageUrl(uploadedImageUrl);
                setFormData(prev => ({ ...prev, imageUrl: uploadedImageUrl })); 
            }
        } catch (error: any) {
            setImageError(error?.response?.data?.message || 'Failed to upload image');
        } finally {
            setIsUploadingImage(false);
        }
    }, []);
    
    // Handle image removal
    const handleImageRemove = async () => {
        if (!imageUrl) return;

        try {
            // Extract the S3 key from the URL
            const url = new URL(imageUrl);
            const s3Key = url.pathname.substring(1); // Remove leading slash

            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            const token = getToken();

            // Call backend to delete from S3
            await axios.delete(`${apiUrl}/upload/image`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { key: s3Key }
            });

            // Clear local state
            setImageUrl("");
            setFormData(prev => ({ ...prev, imageUrl: "" }));
            setImageError("");

            console.log('Image deleted from S3 successfully');
        } catch (error: any) {
            console.error('Failed to delete image from S3:', error);
            setImageError('Failed to delete image from S3');

            // Still clear the UI even if S3 deletion fails
            setImageUrl("");
            setFormData(prev => ({ ...prev, imageUrl: "" }));
        }
    };

    // Handle drag and drop
    const handleDrop = useCallback(( e: React.DragEvent) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleImageUpload(files);
        }
    }, [handleImageUpload]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    // Load dropdown data (shared for add & edit)
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const token = getToken();

                // Get companyId from URL (for SUPERADMIN) or user context (for ADMIN)
                const urlParams = new URLSearchParams(window.location.search);
                const companyId = urlParams.get('companyId');

                // Load Roles
                const rolesRes = await axios.get(`${apiUrl}/roles/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const rolesData = rolesRes.data?.data || rolesRes.data || [];
                const rolesArray = Array.isArray(rolesData) ? rolesData : [];
                if (mounted) setRoles(rolesArray);

                // Load Departments (company-scoped)
                const deptsUrl = companyId 
                    ? `${apiUrl}/co-departments?companyId=${companyId}`
                    : `${apiUrl}/co-departments`;
                const deptsRes = await axios.get(deptsUrl, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                let departments = deptsRes.data?.data || deptsRes.data || [];
                departments = Array.isArray(departments) ? departments : [];
                if (mounted) setDepartments(departments);

                // Load Employee Categories (company-scoped)
                const empCatsUrl = companyId 
                    ? `${apiUrl}/employee-categories?companyId=${companyId}`
                    : `${apiUrl}/employee-categories`;
                const empCatsRes = await axios.get(empCatsUrl, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                let empCategories = empCatsRes.data?.data || empCatsRes.data || [];
                empCategories = Array.isArray(empCategories) ? empCategories : [];
                if (mounted) setEmpCategories(empCategories);

            } catch (err) {
                console.error("Error fetching dropdown data:", err);
            }
        })();
        return () => { mounted = false; };
    }, []);

    // If edit: fetch user and fill the form - still *exact same* UI
    useEffect(() => {
        if (mode !== "edit" || !id) return;
        let mounted = true;
        (async() => {
            try {
                setLoading(true);
                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                const res = await axios.get(
                    `${apiUrl}/users/${id}`,
                    { headers: { Authorization: `Bearer ${getToken()}` }}
                );

                // Map API -> form fields (handle nested response)
                const c = res.data.data || res.data;
                const filled: FormData = {
                    firstName: c.firstName ?? "",
                    lastName: c.lastName ?? "",
                    username: c.username ?? "",
                    password: "", // Don't pre-fill password for security
                    roleId: c.roleId ?? "",
                    status: String(c.status ?? true), // Convert boolean to string
                    mobileNo: c.mobileNo ?? "",
                    email: c.email ?? "",
                    staffId: c.staffId ?? "",
                    position: c.position ?? "",
                    department: c.department?.id ?? "",
                    category: c.empCategory?.id ?? "",
                    joinDate: c.joinDate ? parseLocalDateOnly(c.joinDate) : "",
                    resignDate: c.resignDate ? parseLocalDateOnly(c.resignDate) : "",
                    companyId: c.companyId ?? "",
                    imageUrl: c.imageUrl ?? "",
                };
                if (mounted) {
                    setFormData(filled);
                    setImageUrl(c.imageUrl ?? "");
                    console.log('Set imageUrl state to:', c.imageUrl);
                }

            } catch (err: any) {
                setError(err?.response?.data?.message || "Failed to load user");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [mode, id]);

    const handleSubmit = async () => {
        try {
            setError("");
            setIsSubmitting(true);
            
            // Validate form
            const errors = validateForm();
            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                setError("Please enter the required field(s) above before submitting");
                setIsSubmitting(false);
                return;
            }
            
            const token = getToken();
            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
            
            // Get companyId from URL (for SUPERADMIN) or user context (for ADMIN)
            const urlParams = new URLSearchParams(window.location.search);
            const urlCompanyId = urlParams.get('companyId');
            // Use the form's companyId if available (for edit mode), otherwise fall back to URL
            const companyId = formData.companyId || urlCompanyId;
            
            // Prepare data with proper type conversion and clean empty strings
            const payload = {
                ...formData,
                status: formData.status === "true", // Convert string to boolean
                joinDate: formData.joinDate || null,
                resignDate: formData.resignDate || null,
                companyId: companyId, // Include companyId for user creation
                // Clean empty strings for foreign keys to avoid constraint errors
                roleId: formData.roleId || null,
                departmentId: formData.department || null,
                empCategoryId: formData.category || null,
                email: formData.email || null,
                mobileNo: formData.mobileNo || null,
                position: formData.position || null,
            };

            const url = companyId 
                ? `${apiUrl}/users${mode === "edit" && id ? `/${id}` : ''}?companyId=${companyId}`
                : `${apiUrl}/users${mode === "edit" && id ? `/${id}` : ''}`;

            if (mode === "edit" && id) {
                await axios.put(url, payload, { headers: { Authorization: `Bearer ${token}` }});
            } else {
                await axios.post(url, payload, { headers: { Authorization: `Bearer ${token}` }});
            }
            
            // Navigate back with project/company context
            const searchParams = new URLSearchParams(window.location.search);
            const projectId = searchParams.get("projectId");
            
            let returnUrl = "/users";
            if (projectId) {
                returnUrl += `?projectId=${projectId}`;
            } else if (companyId) {
                returnUrl += `?companyId=${companyId}`;
            }
            
            navigate(returnUrl);
        } catch (err: any) {
            setError(err?.response?.data?.message || "Submission failed");
        } finally {
            setIsSubmitting(false);
        }
    }

    // Helper component for field errors
    const FieldError = ({ error }: { error?: string }) => {
        if (!error) return null;
        return (
            <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                {error}
            </div>
        );
    };

    return (
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
            <div className="col-span-12 sm:col-span-10 sm:col-start-2">
                <div className="flex flex-col lg:items-center lg:flex-row gap-y-2">
                    <div
                        className={clsx([
                            "flex items-center lg:justify-center flex-1 lg:first:justify-start lg:last:justify-end group active",
                            "after:hidden before:hidden after:lg:block before:lg:block",
                            "first:after:content-[''] first:after:w-full first:after:bg-slate-300/60 first:after:h-[2px] first:after:ml-5 group-[.mode--light]:first:after:bg-slate-300/20",
                            "last:before:content-[''] last:before:w-full last:before:bg-slate-300/60 last:before:h-[2px] last:before:mr-5 group-[.mode--light]:last:before:bg-slate-300/20",
                            "last:after:hidden after:content-[''] after:w-full after:bg-slate-300/60 after:h-[2px] after:ml-5 group-[.mode--light]:after:bg-slate-300/20",
                            "first:before:hidden before:content-[''] before:w-full before:bg-slate-300/60 before:h-[2px] before:mr-5 group-[.mode--light]:before:bg-slate-300/20",
                        ])}
                    >
                        <div className="flex items-center">
                            <div className="bg-white border rounded-full group-[.mode--light]:!bg-transparent group-[.active]:bg-primary group-[.active]:text-white group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-white/[0.25] [.group.mode--light_.group.active_&]:!bg-white/[0.12] [.group.mode--light_.group.active_&]:!border-white/[0.15] dark:[.group.mode--light_.group.active_&]:!bg-darkmode-800/30 dark:[.group.mode--light_.group.active_&]:!border-white/10 dark:group-[.mode--light]:!border-white/10 dark:bg-transparent">
                                <div className="flex items-center justify-center w-10 h-10">
                                    1
                                </div>
                            </div>
                            <div className="ml-3.5 group-[.mode--light]:!text-slate-300 font-medium whitespace-nowrap text-slate-500 group-[.active]:text-current [.group.mode--light_.group.active_&]:!text-slate-100">
                                {mode === "edit" ? " Edit " : ""}
                                User Information
                            </div>
                        </div>
                    </div>
                    <div
                        className={clsx([
                            "flex items-center lg:justify-center flex-1 lg:first:justify-start lg:last:justify-end group",
                            "after:hidden before:hidden after:lg:block before:lg:block",
                            "first:after:content-[''] first:after:w-full first:after:bg-slate-300/60 first:after:h-[2px] first:after:ml-5 group-[.mode--light]:first:after:bg-slate-300/20",
                            "last:before:content-[''] last:before:w-full last:before:bg-slate-300/60 last:before:h-[2px] last:before:mr-5 group-[.mode--light]:last:before:bg-slate-300/20",
                            "last:after:hidden after:content-[''] after:w-full after:bg-slate-300/60 after:h-[2px] after:ml-5 group-[.mode--light]:after:bg-slate-300/20",
                            "first:before:hidden before:content-[''] before:w-full before:bg-slate-300/60 before:h-[2px] before:mr-5 group-[.mode--light]:before:bg-slate-300/20",
                        ])}
                    />
                </div>

                <div className="mt-7 space-y-6">
                    {/* Container 1: Mandatory Fields */}
                    <div className="flex flex-col box box--stacked">
                        <div className="p-7">
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                                    Required Information
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                    Please fill in all required fields marked with *
                                </p>
                            </div>

                            
                            {/* === User Image === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">User Image</div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            Upload user profile image. Drag & drop or click to select.
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <div className="flex items-center">
                                        {/* Image Preview */}
                                        <div
                                            className="relative flex items-center justify-center w-24 h-24 border rounded-full bg-primary/5 border-primary/10 cursor-pointer hover:border-primary/30 transition-colors"
                                            onDrop={handleDrop}
                                            onDragOver={handleDragOver}
                                            onClick={() => dropzoneRef.current?.dropzone?.hiddenFileInput?.click()}
                                        >
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt="User profile"
                                                    className="w-full h-full object-cover rounded-full"
                                                    onLoad={() => console.log('Image loaded successfully:', imageUrl)} // ← Add this line
                                                    onError={(e) => console.log('Image failed to load:', imageUrl, e)} // ← Add this line
                                                />
                                            ) : (
                                                <Lucide
                                                    icon="User"
                                                    className="w-[65%] h-[65%] fill-slate-300/70 -mt-1.5 stroke-[0.5] stroke-slate-400/50"
                                                />
                                            )}

                                            {/* Upload/Edit Icon */}
                                            <div className="absolute bottom-0 right-0 flex items-center justify-center rounded-full bg-white border-2 border-primary/20 w-7 h-7 hover:bg-primary/5 transition-colors">
                                                {isUploadingImage ? (
                                                    <Lucide
                                                        icon="Loader2"
                                                        className="w-3.5 h-3.5 stroke-[1.3] text-primary animate-spin"
                                                    />
                                                ) : (
                                                    <Lucide
                                                        icon="Pencil"
                                                        className="w-3.5 h-3.5 stroke-[1.3] text-slate-500"
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Remove Button */}
                                        {imageUrl && (
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="pl-3.5 pr-4 mr-2 h-8 ml-8"
                                                onClick={handleImageRemove}
                                                disabled={isUploadingImage}
                                            >
                                                <Lucide
                                                    icon="Trash2"
                                                    className="w-3.5 h-3.5 mr-1.5 stroke-[1.3]"
                                                />
                                                Remove
                                            </Button>
                                        )}
                                    </div>

                                    {/* Hidden Dropzone for file selection */}
                                    <div className="hidden">
                                        <Dropzone
                                            getRef={(el) => {
                                                dropzoneRef.current = el;
                                            }}
                                            options={{
                                                url: "#", // We'll handle upload manually
                                                autoProcessQueue: false,
                                                acceptedFiles: "image/*",
                                                maxFiles: 1,
                                                maxFilesize: 5, // 5MB
                                                addRemoveLinks: false,
                                                init: function () {
                                                    this.on("addedfile", (file) => {
                                                        handleImageUpload([file]);
                                                    });
                                                }
                                            }}
                                        />
                                    </div>

                                    {/* Error Message */}
                                    {imageError && (
                                        <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                                            {imageError}
                                        </div>
                                    )}
                                </div>
                            </div>

                            

                            {/* === COMPULSORY FIELDS === */}
                            
                            {/* === First Name === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">First Name</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="firstName"
                                        required
                                        placeholder="Enter first name"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                        className={clsx({
                                            "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.firstName
                                        })}
                                    />
                                    <FieldError error={formErrors.firstName} />
                                </div>
                            </div>

                            {/* === Last Name === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Last Name</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="lastName"
                                        required
                                        placeholder="Enter last name"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                        className={clsx({
                                            "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.lastName
                                        })}
                                    />
                                    <FieldError error={formErrors.lastName} />
                                </div>
                            </div>

                            {/* === Username === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Username</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="username"
                                        required
                                        placeholder="Enter username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                        className={clsx({
                                            "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.username
                                        })}
                                    />
                                    <FieldError error={formErrors.username} />
                                </div>
                            </div>

                            {/* === Password === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Password</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                        <div className="mt-1.5 xl:mt-3 text-xs leading-relaxed text-slate-500/80 dark:text-slate-400">
                                            {mode === "edit" ? "Leave blank to keep current password" : "Minimum 8 characters"}
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <div className="relative">
                                        <FormInput
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            required={mode === "add"}
                                            placeholder={mode === "edit" ? "Leave blank to keep current" : "Enter password"}
                                            value={formData.password}
                                            onChange={handleChange}
                                            disabled={loading || isSubmitting}
                                            className={clsx({
                                                "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.password,
                                                "pr-10": true // Add padding for the eye icon
                                            })}
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                                            onClick={togglePasswordVisibility}
                                            disabled={loading || isSubmitting}
                                        >
                                            <Lucide
                                                icon={showPassword ? "EyeOff" : "Eye"}
                                                className="w-4 h-4"
                                            />
                                        </button>
                                    </div>
                                    <FieldError error={formErrors.password} />
                                </div>
                            </div>

                            {/* === Role === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Role</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormSelect
                                        name="roleId"
                                        value={formData.roleId}
                                        onChange={handleChange}
                                        required
                                        disabled={loading || isSubmitting}
                                        className={clsx({
                                            "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.roleId
                                        })}
                                    >
                                        <option value="">Select a role</option>
                                        {Array.isArray(roles) && roles.map((role) => (
                                            <option key={role.id} value={role.id}>
                                                {role.role}
                                            </option>
                                        ))}
                                    </FormSelect>
                                    <FieldError error={formErrors.roleId} />
                                </div>
                            </div>

                            {/* === Status === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Status</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Required
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormSelect
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        required
                                        disabled={loading || isSubmitting}
                                        className={clsx({
                                            "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.status
                                        })}
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </FormSelect>
                                    <FieldError error={formErrors.status} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Container 2: Optional Fields */}
                    <div className="flex flex-col box box--stacked">
                        <div className="p-7">
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                                    Additional Information
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                    Optional fields to provide more details about the user
                                </p>
                            </div>

                            {/* === OPTIONAL FIELDS === */}

                            {/* === Mobile Number === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Mobile Number</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="tel"
                                        name="mobileNo"
                                        placeholder="e.g., 012-3456789"
                                        value={formData.mobileNo}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                    />
                                </div>
                            </div>

                            {/* === Email === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Email</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="email"
                                        name="email"
                                        placeholder="e.g., user@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                        className={clsx({
                                            "border-red-500 focus:border-red-500 focus:ring-red-500": formErrors.email
                                        })}
                                    />
                                    <FieldError error={formErrors.email} />
                                </div>
                            </div>

                            {/* === Staff ID === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Staff ID</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="staffId"
                                        placeholder="e.g., EMP001"
                                        value={formData.staffId}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                    />
                                </div>
                            </div>

                            {/* === Position === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Position</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="text"
                                        name="position"
                                        placeholder="e.g., Sales Executive"
                                        value={formData.position}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                    />
                                </div>
                            </div>

                            {/* === Department === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Department</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormSelect
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                    >
                                        <option value="">Select a department</option>
                                        {Array.isArray(departments) && departments.map((dept) => (
                                            <option key={dept.id} value={dept.id}>
                                                {dept.department}
                                            </option>
                                        ))}
                                    </FormSelect>
                                </div>
                            </div>

                            {/* === Employee Category === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Employee Category</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormSelect
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                    >
                                        <option value="">Select a category</option>
                                        {Array.isArray(empCategories) && empCategories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.category}
                                            </option>
                                        ))}
                                    </FormSelect>
                                </div>
                            </div>

                            {/* === Join Date === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Join Date</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="date"
                                        name="joinDate"
                                        value={formData.joinDate}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                    />
                                </div>
                            </div>

                            {/* === Resign Date === */}
                            <div className="flex-col block pt-5 mt-5 xl:items-center sm:flex xl:flex-row first:mt-0 first:pt-0">
                                <label className="inline-block mb-2 sm:mb-0 sm:mr-5 sm:text-right xl:w-60 xl:mr-14">
                                    <div className="text-left">
                                        <div className="flex items-center">
                                            <div className="font-medium">Resign Date</div>
                                            <div className="ml-2.5 px-2 py-0.5 bg-slate-100 text-slate-500 dark:bg-darkmode-300 dark:text-slate-400 text-xs rounded-md border border-slate-200">
                                                Optional
                                            </div>
                                        </div>
                                    </div>
                                </label>
                                <div className="flex-1 w-full mt-3 xl:mt-0">
                                    <FormInput
                                        type="date"
                                        name="resignDate"
                                        value={formData.resignDate}
                                        onChange={handleChange}
                                        disabled={loading || isSubmitting}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="mt-6 text-danger text-sm">{error}</div>
                            )}
                        </div>

                        <div className="flex py-5 border-t md:justify-end px-7 border-slate-200/80">
                            <Button
                                variant="outline-primary"
                                className="w-full px-10 md:w-auto border-primary/50"
                                onClick={handleSubmit}
                                disabled={loading || isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Lucide icon="Loader2" className="stroke-[1.3] w-4 h-4 mr-2 -ml-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Lucide icon="Pocket" className="stroke-[1.3] w-4 h-4 mr-2 -ml-2" />
                                        Save
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}